// src/pages/NotificationRecipients.js
//
// Email Notification Recipients.
//
// Stores one doc per module at notificationRecipients/{moduleKey}:
//   { userIds: string[], emails: string[], enabled: boolean }
// `emails` is always derived from the selected users' email addresses, because
// that is the field the Cloud Functions read when sending reminder emails.

import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDocs, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { getStaffDisplayName, isStaffDeactivated } from "../utils/staffSettingsUtils";
import "./NotificationRecipients.css";

const MODULES = [
  {
    key: "licenseCertificates",
    label: "License Certificates",
    description:
      "Daily reminders before and after a license or certificate expiration date.",
  },
  {
    key: "contracts",
    label: "Contracts",
    description: "Daily reminders before and after a contract's end date.",
  },
  {
    key: "contractors",
    label: "Contractors",
    description:
      "Reminders when a contractor's general liability, commercial auto or workers comp insurance is about to expire or has expired.",
  },
  {
    key: "vehicles",
    label: "Vehicles",
    description:
      "Vehicle inspection, registration and service follow-up reminders. Staff with \"Receive Vehicle Expiration Emails\" set to Yes on Staff Settings also receive these.",
  },
  {
    key: "timeOff",
    label: "Time Off",
    description:
      "A daily email for every pending time off request until it is approved or denied.",
  },
  {
    key: "notesTasks",
    label: "Notes / Tasks",
    description:
      "An email whenever a new task is created from Team Messaging.",
  },
  {
    // Sent by stripeWebhook in functions/index.js when a customer pays an
    // invoice through the Stripe "Pay Online" link.
    key: "stripePayments",
    label: "Stripe Online Payments",
    description:
      "An email when a customer pays an invoice online: card payment received, bank (ACH) payment pending, bank payment cleared, or bank payment failed.",
    fallbackNote:
      "No one selected yet, so these emails go to team@prioritypestdefense.com.",
  },
];

const emptyModuleSettings = () => ({ userIds: [], emails: [], enabled: true });

const sameIds = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
};

const NotificationRecipients = () => {
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({});
  const [savedSettings, setSavedSettings] = useState({});
  const [openModule, setOpenModule] = useState(null);
  const [searchByModule, setSearchByModule] = useState({});
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [savingKey, setSavingKey] = useState("");
  const [savedKey, setSavedKey] = useState("");

  useEffect(() => {
    const loadData = async () => {
      try {
        const usersSnap = await getDocs(collection(db, "users"));
        const usersList = usersSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) =>
            getStaffDisplayName(a).localeCompare(getStaffDisplayName(b), undefined, {
              sensitivity: "base",
            })
          );
        setUsers(usersList);

        const nextSettings = {};
        for (const module of MODULES) {
          const snap = await getDoc(doc(db, "notificationRecipients", module.key));
          const data = snap.exists() ? snap.data() : emptyModuleSettings();
          nextSettings[module.key] = {
            ...emptyModuleSettings(),
            ...data,
            userIds: Array.isArray(data.userIds) ? data.userIds : [],
            emails: Array.isArray(data.emails) ? data.emails : [],
          };
        }

        setSettings(nextSettings);
        setSavedSettings(nextSettings);
      } catch (err) {
        console.error("NotificationRecipients: load failed", err);
        setLoadError(err?.message || "Unable to load notification settings.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const usersById = useMemo(() => {
    const map = {};
    users.forEach((u) => {
      map[u.id] = u;
    });
    return map;
  }, [users]);

  const emailsForUserIds = (userIds = []) =>
    userIds
      .map((id) => usersById[id]?.email)
      .map((email) => String(email || "").trim())
      .filter(Boolean);

  const isDirty = (moduleKey) => {
    const cur = settings[moduleKey];
    const saved = savedSettings[moduleKey];
    if (!cur || !saved) return false;
    return (
      (cur.enabled !== false) !== (saved.enabled !== false) ||
      !sameIds(cur.userIds || [], saved.userIds || [])
    );
  };

  const updateModule = (moduleKey, updater) => {
    setSavedKey("");
    setSettings((prev) => {
      const current = prev[moduleKey] || emptyModuleSettings();
      const next = updater(current);
      return {
        ...prev,
        [moduleKey]: {
          ...next,
          emails: emailsForUserIds(next.userIds || []),
        },
      };
    });
  };

  const toggleUser = (moduleKey, user) => {
    updateModule(moduleKey, (current) => {
      const ids = current.userIds || [];
      const userIds = ids.includes(user.id)
        ? ids.filter((id) => id !== user.id)
        : [...ids, user.id];
      return { ...current, userIds };
    });
  };

  const setEnabled = (moduleKey, enabled) => {
    updateModule(moduleKey, (current) => ({ ...current, enabled }));
  };

  const selectVisible = (moduleKey, visibleUsers, select) => {
    updateModule(moduleKey, (current) => {
      const ids = new Set(current.userIds || []);
      visibleUsers.forEach((u) => {
        if (select) ids.add(u.id);
        else ids.delete(u.id);
      });
      return { ...current, userIds: Array.from(ids) };
    });
  };

  const resetModule = (moduleKey) => {
    setSettings((prev) => ({ ...prev, [moduleKey]: savedSettings[moduleKey] }));
  };

  const saveModule = async (moduleKey) => {
    const current = settings[moduleKey] || emptyModuleSettings();
    const payload = {
      userIds: current.userIds || [],
      emails: emailsForUserIds(current.userIds || []),
      enabled: current.enabled !== false,
    };

    setSavingKey(moduleKey);
    try {
      await setDoc(doc(db, "notificationRecipients", moduleKey), payload, { merge: true });
      setSettings((prev) => ({ ...prev, [moduleKey]: { ...current, ...payload } }));
      setSavedSettings((prev) => ({ ...prev, [moduleKey]: { ...current, ...payload } }));
      setSavedKey(moduleKey);
    } catch (err) {
      console.error("NotificationRecipients: save failed", err);
      alert(`Unable to save recipients: ${err?.message || err}`);
    } finally {
      setSavingKey("");
    }
  };

  const visibleUsersFor = (moduleKey) => {
    const term = String(searchByModule[moduleKey] || "").trim().toLowerCase();
    const selected = new Set(settings[moduleKey]?.userIds || []);

    return users.filter((u) => {
      // Always keep already-selected people visible so they can be removed.
      if (!showInactive && isStaffDeactivated(u) && !selected.has(u.id)) return false;
      if (!term) return true;
      const haystack = [getStaffDisplayName(u), u.email || "", u.role || "", u.position || ""]
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  };

  return (
    <div className="notif-page">
      <div className="notif-header">
        <div>
          <h2 className="notif-title">Email Notification Recipients</h2>
          <p className="notif-subtitle">
            Choose who receives the automated reminder and task emails for each area of the
            app. Turn a module off to stop its emails without losing the recipient list.
          </p>
        </div>
        <label className="notif-inline-check">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show deactivated staff
        </label>
      </div>

      {loadError ? <div className="notif-alert">Could not load: {loadError}</div> : null}
      {loading ? <div className="notif-empty">Loading notification settings...</div> : null}

      {!loading && (
        <div className="notif-grid">
          {MODULES.map((module) => {
            const moduleSettings = settings[module.key] || emptyModuleSettings();
            const enabled = moduleSettings.enabled !== false;
            const selectedIds = moduleSettings.userIds || [];
            const recipientEmails = emailsForUserIds(selectedIds);
            const isOpen = openModule === module.key;
            const dirty = isDirty(module.key);
            const visibleUsers = isOpen ? visibleUsersFor(module.key) : [];
            const missingEmailCount = selectedIds.filter((id) => !usersById[id]?.email).length;

            return (
              <section
                key={module.key}
                className={`notif-card${isOpen ? " notif-card-open" : ""}${
                  enabled ? "" : " notif-card-off"
                }`}
              >
                <div className="notif-card-head">
                  <div className="notif-card-titles">
                    <h3 className="notif-card-title">{module.label}</h3>
                    <p className="notif-card-desc">{module.description}</p>
                  </div>

                  <label className="notif-switch" title={enabled ? "Emails on" : "Emails off"}>
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => setEnabled(module.key, e.target.checked)}
                    />
                    <span className="notif-switch-track" aria-hidden="true">
                      <span className="notif-switch-thumb" />
                    </span>
                    <span className="notif-switch-label">{enabled ? "On" : "Off"}</span>
                  </label>
                </div>

                <div className="notif-card-meta">
                  <span className={`notif-pill ${enabled ? "notif-pill-on" : "notif-pill-off"}`}>
                    {enabled ? "Enabled" : "Disabled"}
                  </span>
                  <span className="notif-pill">
                    {recipientEmails.length} recipient{recipientEmails.length === 1 ? "" : "s"}
                  </span>
                  {dirty ? <span className="notif-pill notif-pill-warn">Unsaved changes</span> : null}
                  {savedKey === module.key && !dirty ? (
                    <span className="notif-pill notif-pill-on">Saved</span>
                  ) : null}
                </div>

                {recipientEmails.length ? (
                  <div className="notif-chips">
                    {recipientEmails.slice(0, isOpen ? recipientEmails.length : 4).map((email) => (
                      <span key={email} className="notif-chip">
                        {email}
                      </span>
                    ))}
                    {!isOpen && recipientEmails.length > 4 ? (
                      <span className="notif-chip notif-chip-more">
                        +{recipientEmails.length - 4} more
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <div className="notif-none">
                    {module.fallbackNote || "No recipients selected."}
                  </div>
                )}

                {missingEmailCount ? (
                  <div className="notif-warn">
                    {missingEmailCount} selected staff member
                    {missingEmailCount === 1 ? " has" : "s have"} no email address and will not
                    receive emails.
                  </div>
                ) : null}

                <div className="notif-card-actions">
                  <button
                    type="button"
                    className="notif-btn notif-btn-ghost"
                    onClick={() => setOpenModule(isOpen ? null : module.key)}
                  >
                    {isOpen ? "Hide Recipients" : "Edit Recipients"}
                  </button>
                  {dirty ? (
                    <button
                      type="button"
                      className="notif-btn notif-btn-ghost"
                      onClick={() => resetModule(module.key)}
                      disabled={savingKey === module.key}
                    >
                      Discard
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="notif-btn notif-btn-primary"
                    onClick={() => saveModule(module.key)}
                    disabled={!dirty || savingKey === module.key}
                  >
                    {savingKey === module.key ? "Saving..." : "Save"}
                  </button>
                </div>

                {isOpen && (
                  <div className="notif-picker">
                    <div className="notif-picker-bar">
                      <input
                        type="text"
                        className="notif-search"
                        placeholder="Search name, email, role..."
                        value={searchByModule[module.key] || ""}
                        onChange={(e) =>
                          setSearchByModule((prev) => ({ ...prev, [module.key]: e.target.value }))
                        }
                      />
                      <button
                        type="button"
                        className="notif-btn notif-btn-small"
                        onClick={() => selectVisible(module.key, visibleUsers, true)}
                        disabled={!visibleUsers.length}
                      >
                        Select shown
                      </button>
                      <button
                        type="button"
                        className="notif-btn notif-btn-small"
                        onClick={() => selectVisible(module.key, visibleUsers, false)}
                        disabled={!visibleUsers.length}
                      >
                        Clear shown
                      </button>
                    </div>

                    <div className="notif-user-list">
                      {!visibleUsers.length ? (
                        <div className="notif-empty">No staff match this search.</div>
                      ) : null}
                      {visibleUsers.map((user) => {
                        const checked = selectedIds.includes(user.id);
                        const inactive = isStaffDeactivated(user);
                        return (
                          <label
                            key={user.id}
                            className={`notif-user${checked ? " notif-user-checked" : ""}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleUser(module.key, user)}
                            />
                            <span className="notif-user-text">
                              <span className="notif-user-name">
                                {getStaffDisplayName(user) || "(No name)"}
                                {inactive ? <span className="notif-tag">Deactivated</span> : null}
                              </span>
                              <span className="notif-user-email">
                                {user.email || "No email on file"}
                              </span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificationRecipients;
